import { FC, useRef } from 'react'
import { useBoolean, useClickAway } from 'ahooks'

import { Icon } from '@trustycars/genie-ui/icons/Icon'
import { Button, Spinner } from '@trustycars/genie-ui/components'
import { TagsNoRef } from '@trustycars/genie-ui/types'

import AccountPreview, { AccountPreviewProps } from '$elements/AccountPreview'
import AppInfo from '$elements/AppInfo'

import useSignal from '$actions/useSignal'
import useAppDetail from '$actions/useAppDetail'
import useTokenProfileLogout from '$actions/useTokenProfileLogout'

import { Profile } from '$api/types'
import { getAlias } from '$services/user'

import { TokenDetail } from '$store/session/tokens'
import { with_tokens } from '$store/profiles'
import queryParams from '$store/queryParams'
import { getPhone } from '$app/utilities'

import { title as titleStyle } from '$elements/AppInfo/styles.css'
import ExtraIcon from '$icons/ExtraIcon'

import {
  header,
  container,
  main,
  list,
  loaderControl,
  footer,
  addNew,
  manage,
  nav,
  options,
  previewMain,
  previewContainer,
  manageIcon,
  footerTop,
  footerBottom,
  forgotLink
} from './styles.css'
import SeparatorWithLabel from '$elements/SeparatorWithLabel'
import Link from '$elements/Link'

export type AccountSelectionProps = {
  defaultTitle?: string
  defaultDescription?: string
  onSelect: (user_id: TokenDetail['user_id']) => void
  onAddNew: VoidFunction
}

export const AccountSelection: FC<AccountSelectionProps> = ({
  defaultTitle = 'Choose an account',
  onSelect,
  onAddNew
}) => {
  const removeButtonRef = useRef(null)
  const listRef = useRef(null)

  const {
    params: { client_id }
  } = useSignal(queryParams)
  const targetApp = useAppDetail(client_id)
  const isAppTarget = !!client_id
  const isAppInfoLoading = isAppTarget && targetApp.loading

  // TODO: Revert this with the following component after launchpad API is working properly
  const isAppLogin = false
  // const isAppLogin = isAppInfoLoading || !!targetApp.data

  const appTitle = targetApp.data?.data.name
  const appLogo = targetApp.data?.data.logo
  const appDomain = targetApp.data?.data.domain

  const [isManageable, { toggle, setFalse }] = useBoolean()
  const data = useSignal(with_tokens)

  const hasTokens = !!data.length

  const manageProps: TagsNoRef<'button'> = {
    title: 'Logout or Remove account',
    type: 'button',
    className: manage({ active: isManageable }),
    onClick: e => {
      e.stopPropagation()
      toggle()
    }
  }

  useClickAway(setFalse, [listRef, removeButtonRef])

  return (
    <section className={container} data-testid="account-selection">
      <header className={header}>
        {isAppLogin && (
          <AppInfo
            isLoading={isAppInfoLoading}
            domain={appDomain}
            logo={appLogo}
            title={appTitle}
          />
        )}

        {!isAppLogin && <h2 className={titleStyle}>{defaultTitle}</h2>}
      </header>

      <nav className={nav}>
        <Button
          styleVariants={{ type: 'text', size: 'small' }}
          name="use-another-account"
          className={addNew}
          onClick={onAddNew}
        >
          Use another account
          <Icon namespace="Add" />
        </Button>
      </nav>

      {hasTokens && (
        <main className={main}>
          <div className="sticky fade-out" />

          <div className={list} ref={listRef}>
            {data.map(({ id, loading, data: profile, token }) => (
              <Preview
                key={id}
                isManageable={isManageable}
                containerProps={{
                  onClick: () => onSelect(id)
                }}
                loading={loading}
                profile={profile}
                token={token}
              />
            ))}
          </div>

          <div className="sticky fade-in" />
        </main>
      )}

      <footer className={footer}>
        <div className={footerTop}>
          <button
            name={isManageable ? 'cancel-remove-account' : 'remove-account'}
            ref={removeButtonRef}
            {...manageProps}
          >
            {!isManageable && (
              <>
                <ExtraIcon name="grear" className={manageIcon} />
                <span>Remove an account</span>
              </>
            )}
            {isManageable && 'Cancel'}
          </button>
        </div>

        <SeparatorWithLabel label="or" />

        <div className={footerBottom}>
          <Link
            to="authForgotPassword"
            options={({ queryParams }) => ({ queryParams })}
            className={forgotLink}
          >
            Forgot password?
          </Link>
        </div>
      </footer>
    </section>
  )
}

type PreviewProps = Pick<AccountPreviewProps, 'containerProps'> & {
  isManageable: boolean
  loading?: boolean
  profile?: Profile
  token?: TokenDetail['token']
}

const Preview: FC<PreviewProps> = ({
  token,
  containerProps,
  isManageable,
  profile,
  loading: profileRefreshing
}) => {
  const { loading: logoutLoading, logout } = useTokenProfileLogout(
    token,
    profile
  )

  const isLoading = logoutLoading || profileRefreshing

  const disabled = isManageable || isLoading
  const disabledRemove = !isManageable || isLoading

  return (
    <article className={previewContainer}>
      {isLoading && (
        <div className={loaderControl}>
          <Spinner />
        </div>
      )}

      {profile && (
        <main
          className={previewMain({ inactivePreview: isManageable })}
          data-testid={`account-id:${profile.id} email:${profile.email}`}
        >
          <AccountPreview
            name={profile.name}
            username={getAlias({
              email: profile.email,
              phone: getPhone(profile)
            })}
            image={profile.profile_photo_url}
            containerProps={{ ...containerProps, disabled, name: 'select' }}
          />

          <aside className={options({ active: isManageable })}>
            <Button
              name="logout"
              styleVariants={{
                size: 'small',
                kind: 'danger',
                type: 'text',
                category: 'icon'
              }}
              title="Logout this session"
              disabled={disabledRemove}
              onClick={logout}
            >
              {logoutLoading ? (
                <Spinner color="inherit" />
              ) : (
                <Icon namespace="Trash" />
              )}
            </Button>
          </aside>
        </main>
      )}
    </article>
  )
}

export default AccountSelection
